/**
 * Panel Audio Module — auto-play O-Ton per panel
 *
 * Purpose:
 * - Plays a panel's <audio class="story-panel-audio" data-audio="URL"> when the
 *   panel becomes active; pauses (keeping position) on deactivation; resumes
 *   from the saved position on return  (Nadelöhr-Storyboard Decision 01).
 * - Sequence: multiple .story-panel-audio with data-panel-audio-sequence="N"
 *   play one after another  (Nadelöhr Sync-Panel P09).
 * - Single-medium enforced via StoryMediaManager.
 *
 * Activation: <audio class="story-panel-audio" data-audio="URL"> inside a panel.
 * Generic — built for Nadelöhr, reusable for any panel-level narration.
 *
 * Module Contract: init(panel) / start() / pause() / stop()  — MediaManager-kompatibel.
 * Dependencies: StoryMediaManager (panel events laufen über den Manager).
 * File: assets/js/story/story-module-panel-audio.js
 */
(function (window) {
  'use strict';

  var DEBUG = window.location.search.indexOf('debug=story') !== -1;

  // Stilles 1-Sample-WAV (identisch zu story-media-manager.js) — dient zum iOS-Unlock:
  // ein play() während der User-Geste „segnet" das Element, danach erlaubt iOS Safari
  // auch programmatische (scroll-getriebene) play()-Aufrufe — selbst nach src-Wechsel.
  var SILENT_WAV = 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=';

  function log() {
    if (DEBUG && window.console) {
      console.log.apply(console, ['[PanelAudio]'].concat([].slice.call(arguments)));
    }
  }

  function PanelAudioModule() {
    // Module-Contract-Properties
    this.name = 'panel-audio';
    this.type = 'audio';
    this.isPlaying = false;
    this.panel = null;
    this.element = null;
    this._unlocked = false;     // iOS-Audio-Unlock einmalig (s. unlockAudio)
    this._muted = false;        // globaler Mute (vom Atmo-Toggle mitgesteuert, s. setMuted)

    // interner Zustand
    this.currentAudio = null;
    this.queue = [];
    this.queueIndex = 0;
    this.positions = {};        // data-audio-URL -> currentTime (iOS-Resume-Versicherung)
    this._endedEl = null;
    this._onEnded = this._onEnded.bind(this);

    this._init();
  }

  PanelAudioModule.prototype._init = function () {
    var self = this;
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () { self._register(); });
    } else {
      self._register();
    }
  };

  PanelAudioModule.prototype._register = function () {
    if (window.StoryMediaManager) {
      window.StoryMediaManager.register(this);
      log('registriert beim MediaManager');
    } else {
      log('StoryMediaManager fehlt — Panel-Audio inaktiv');
    }
  };

  /* ----------------------------------------------------------
     Module Contract — vom MediaManager aufgerufen
     ---------------------------------------------------------- */

  // init(panel): wird bei story:panel:activated für jedes Modul gefeuert.
  PanelAudioModule.prototype.init = function (panel) {
    this.panel = panel;

    var found = panel.querySelectorAll('.story-panel-audio');
    if (!found.length) return;   // Panel ohne Audio — nichts zu tun

    // Abspiel-Reihenfolge: data-panel-audio-sequence (Default 1)
    this.queue = [].slice.call(found).sort(function (a, b) {
      return (parseInt(a.getAttribute('data-panel-audio-sequence'), 10) || 1) -
             (parseInt(b.getAttribute('data-panel-audio-sequence'), 10) || 1);
    });
    this.queueIndex = 0;

    // Single-Medium-Gate
    if (window.StoryMediaManager && !window.StoryMediaManager.requestPlay(this)) return;

    this._playCurrent();
  };

  // start(): Contract-Stub — Panel-Audio startet über init(), nicht über User-Geste.
  PanelAudioModule.prototype.start = function () {};

  // unlockAudio(): vom MediaManager bei der ersten User-Geste aufgerufen
  // (StoryMediaManager.enableAutoplay → mod.unlockAudio). iOS-Safari-Fix:
  // Der Panel-O-Ton wird scroll-getrieben (Event 'nb:video:shown', ~920 ms nach
  // dem Snap) programmatisch gestartet — also AUSSERHALB einer User-Geste. iOS
  // blockt das, solange das jeweilige <audio>-Element nie in einer Geste lief
  // (darum spielte nur das Intro + Navi-Tap). Hier „primen" wir JEDES Panel-Audio
  // einmalig in der Geste mit einem stillen WAV: kurz play()→pause(). Danach gilt
  // das Element als entsperrt, auch nach späterem src-Wechsel auf die echte Datei.
  // Still statt der echten Datei → kein hörbares Mehrfach-Ton-Problem (anders als
  // Gallerys altem play()-all-Ansatz mit volume=0).
  PanelAudioModule.prototype.unlockAudio = function () {
    if (this._unlocked) return;
    var els = document.querySelectorAll('.story-panel-audio');
    if (!els.length) return;          // Self-Guard: andere Stories (z.B. Beendorf) unberührt
    this._unlocked = true;

    [].forEach.call(els, function (el) {
      if (el.dataset.iosPrimed === '1') return;
      // Aktives/spielendes Element NICHT anfassen (Intro läuft bereits in der Geste).
      var panel = el.closest ? el.closest('.story-panel') : null;
      if (panel && panel.classList.contains('is-active')) return;
      if (!el.paused) return;
      el.dataset.iosPrimed = '1';
      try {
        el.muted = false;
        el.setAttribute('src', SILENT_WAV);
        var p = el.play();
        if (p && p.then) {
          p.then(function () {
            // Nur pausieren, wenn _playCurrent das Element nicht inzwischen auf die
            // echte Datei umgestellt hat (Race-Schutz fürs gerade aktive Panel).
            if (el.getAttribute('src') === SILENT_WAV) {
              el.pause();
              try { el.currentTime = 0; } catch (e) {}
            }
          }).catch(function () {});
        } else {
          el.pause();
        }
      } catch (e) { /* still */ }
    });
    log('iOS-Unlock: Panel-Audios geprimed', els.length);
  };

  // setMuted(m): globaler Mute für den Panel-O-Ton. Wird vom Atmo-Toggle (Block 6
  // im nb-Modul) mitgerufen, damit der Knopf oben links ALLES stummschaltet
  // (Atmo + Hauptton), nicht nur die Atmo. Muten statt Pausieren: Position läuft
  // weiter, Unmute (im Klick = User-Geste) spielt iOS-konform sofort hörbar weiter.
  PanelAudioModule.prototype.setMuted = function (m) {
    this._muted = !!m;
    if (this.currentAudio) this.currentAudio.muted = this._muted;
  };

  // pause(): vom MediaManager bei Panel-Wechsel / anderem Medium aufgerufen.
  PanelAudioModule.prototype.pause = function () {
    var el = this.currentAudio;
    if (el) {
      this.positions[el.getAttribute('data-audio')] = el.currentTime;   // Resume-Position sichern
      el.pause();
    }
    this.isPlaying = false;
    if (window.StoryMediaManager) window.StoryMediaManager.notifyStop(this);
    log('pausiert — Position gesichert');
  };

  // stop(): voller Stopp mit Reset.
  PanelAudioModule.prototype.stop = function () {
    var el = this.currentAudio;
    if (el) {
      el.pause();
      try { el.currentTime = 0; } catch (e) {}
      this.positions[el.getAttribute('data-audio')] = 0;
    }
    this.isPlaying = false;
    if (window.StoryMediaManager) window.StoryMediaManager.notifyStop(this);
  };

  /* ----------------------------------------------------------
     Wiedergabe
     ---------------------------------------------------------- */

  PanelAudioModule.prototype._playCurrent = function () {
    var el = this.queue[this.queueIndex];
    if (!el) return;

    var src = el.getAttribute('data-audio');
    if (!src) { this._advance(); return; }              // leerer Slot — überspringen
    // Lazy-Load der echten Datei. !== statt !src, damit nach dem iOS-Unlock-Prime
    // (src = SILENT_WAV) auf die echte Datei gewechselt wird. Bei Rückkehr aufs
    // selbe Panel ist src bereits korrekt → kein Reset, Resume-Position bleibt.
    if (el.getAttribute('src') !== src) el.setAttribute('src', src);

    // Resume aus gesicherter Position (Versicherung gegen iOS-Decoder-Unload)
    var pos = this.positions[src];
    if (pos && isFinite(pos)) { try { el.currentTime = pos; } catch (e) {} }

    // genau ein ended-Listener
    if (this._endedEl) this._endedEl.removeEventListener('ended', this._onEnded);
    el.addEventListener('ended', this._onEnded);
    this._endedEl = el;

    this.currentAudio = el;
    this.element = el;
    this.isPlaying = true;
    el.muted = this._muted;     // globalen Mute-Zustand auf neues Panel übertragen

    var p = el.play();
    if (p && p.catch) p.catch(function () { log('Autoplay blockiert — wartet auf User-Geste'); });
    log('spielt', src);
  };

  PanelAudioModule.prototype._onEnded = function () {
    var el = this.currentAudio;
    if (el) {
      try { el.currentTime = 0; } catch (e) {}
      this.positions[el.getAttribute('data-audio')] = 0;   // Decision 01: läuft nicht über das Panel hinaus
    }
    this._advance();
  };

  PanelAudioModule.prototype._advance = function () {
    this.queueIndex++;
    if (this.queue[this.queueIndex]) { this._playCurrent(); return; }
    // Queue durch — NICHT neu starten (Decision 01: erst beim erneuten Aufruf von vorn)
    this.isPlaying = false;
    if (window.StoryMediaManager) window.StoryMediaManager.notifyStop(this);
  };

  /* ----------------------------------------------------------
     Globale Instanz (selbst-registrierend, wie audio-bubbles.js)
     ---------------------------------------------------------- */

  window.StoryModulePanelAudio = new PanelAudioModule();

})(window);
